import type { ReactElement } from 'react';

/**
 * Neutral, built-in fallback for the reporting image panel (docs/
 * momentum-v3-slides.md `reporting`; slide audit Slide 20 item 4). Used only
 * when the rep hasn't set a `content.imageUrl` - never a substitute for a
 * real photo once one exists (same "baked-in defaults must be neutral" rule
 * as every other v3 hero slot, momentum-v3-slides.md global rule 6).
 *
 * A generic KPI-dashboard illustration: three stat tiles, one trend line
 * with a soft fill, and a short bar row - inline SVG (crisp at any panel
 * size, no raster asset to accidentally contaminate with another deck's
 * text or logos, unlike the old `reporting-dashboard.png`). Colours come
 * from the deck's own theme tokens (`--mv3-green-deep` / `--mv3-light-*`),
 * never hard-coded brand colour. This always renders on the light
 * `--mv3-light-bg` rect above, so it uses `--mv3-green-deep` rather than raw
 * `--mv3-mint` throughout (global light-surface colour rule).
 *
 * K3d: the three stat tiles used to carry hardcoded numerals ('128', '64%',
 * '24') that were pure decoration but read as real forecasts on a
 * client-facing deck. There is no real per-proposal number to bind them to
 * here (this is a generic illustration, not a data visualization - the
 * actual forecast lives on `projections`/`tactic_measurement`), so the
 * fabricated figures are gone; each tile is now a plain icon-and-label
 * shape (a small glyph + a bar), same layout, no numerals.
 */
export function ReportingDashboardArt({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      viewBox="0 0 480 360"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
    >
      <rect width="480" height="360" fill="var(--mv3-light-bg)" />

      {/* Three stat tiles - shapes only, no invented figures (K3d). */}
      {[32, 180, 328].map((x) => (
        <g key={x}>
          <rect
            x={x}
            y={32}
            width={120}
            height={84}
            rx={14}
            fill="var(--mv3-light-bg-2)"
            stroke="var(--mv3-light-hairline)"
          />
          <rect x={x + 20} y={54} width={28} height={6} rx={3} fill="var(--mv3-green-deep)" />
          <rect x={x + 20} y={76} width={80} height={10} rx={5} fill="var(--mv3-light-hairline)" />
          <rect x={x + 20} y={94} width={52} height={10} rx={5} fill="var(--mv3-light-hairline)" />
        </g>
      ))}

      {/* Trend line with a soft fill beneath */}
      <g>
        <defs>
          <linearGradient id="mv3-reporting-trend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--mv3-green-deep)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--mv3-green-deep)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M32,236 L112,214 L192,222 L272,176 L352,188 L448,140 L448,252 L32,252 Z"
          fill="url(#mv3-reporting-trend-fill)"
        />
        <polyline
          points="32,236 112,214 192,222 272,176 352,188 448,140"
          fill="none"
          stroke="var(--mv3-green-deep)"
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Short bar row */}
      <g>
        {[52, 88, 68, 104, 76, 96].map((height, index) => {
          const x = 32 + index * 70;
          const y = 328 - height;
          return (
            <rect
              key={x}
              x={x}
              y={y}
              width={40}
              height={height}
              rx={6}
              fill={index === 3 ? 'var(--mv3-green-deep)' : 'var(--mv3-light-hairline)'}
            />
          );
        })}
      </g>
    </svg>
  );
}
